import { useState, useEffect, useCallback } from 'react'
import {
  Search,
  FileText,
  Hash,
  ClipboardCheck,
  AlertTriangle,
  BookOpen,
  Loader2,
  X,
  Filter,
  ExternalLink,
  ChevronRight,
  Info,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import api from '@/services/api'

// ── Interfaces ──────────────────────────────────────────────────────────────

type ResultCategory = 'manual_reference' | 'part_number' | 'inspection_card' | 'non_routine' | 'ata_chapter'

interface SearchResult {
  id: string
  category: ResultCategory
  relevanceScore: number
  citation: string
  // Manual Reference fields
  document?: string
  section?: string
  page?: string
  excerpt?: string
  // Part Number fields
  partNumber?: string
  description?: string
  crossReferences?: string[]
  // Inspection Card fields
  cardNumber?: string
  task?: string
  interval?: string
  // Non-Routine fields
  nrDescription?: string
  tailNumber?: string
  date?: string
  // ATA Chapter fields
  chapter?: string
  title?: string
  chapterDescription?: string
}

type FilterCategory = 'all' | ResultCategory

interface CategoryFilter {
  key: FilterCategory
  label: string
  icon: React.ElementType
  color: string
}

// ── Category Filters ────────────────────────────────────────────────────────

const categoryFilters: CategoryFilter[] = [
  { key: 'all', label: 'All', icon: Search, color: 'bg-gray-100 text-gray-800' },
  { key: 'manual_reference', label: 'Manual References', icon: FileText, color: 'bg-blue-100 text-blue-800' },
  { key: 'part_number', label: 'Part Numbers', icon: Hash, color: 'bg-green-100 text-green-800' },
  { key: 'inspection_card', label: 'Inspection Cards', icon: ClipboardCheck, color: 'bg-orange-100 text-orange-800' },
  { key: 'non_routine', label: 'Non-Routine Items', icon: AlertTriangle, color: 'bg-red-100 text-red-800' },
  { key: 'ata_chapter', label: 'ATA Chapters', icon: BookOpen, color: 'bg-purple-100 text-purple-800' },
]

// ── Sample Data ─────────────────────────────────────────────────────────────

const sampleResults: SearchResult[] = [
  {
    id: '1',
    category: 'manual_reference',
    relevanceScore: 0.97,
    citation: 'Honda HA-420 AMM, Rev J, Chapter 72-00-00, Page 201',
    document: 'HA-420 Aircraft Maintenance Manual (AMM)',
    section: 'Chapter 72-00-00 — Engine General',
    page: 'Page 201–205',
    excerpt: 'GE Honda HF120 turbofan engine removal and installation procedure. Refer to GE Honda CSP for on-condition maintenance intervals. Torque engine mount bolts to 45 ft-lbs per Maintenance Practice 71-00-00.',
  },
  {
    id: '2',
    category: 'part_number',
    relevanceScore: 0.94,
    citation: 'Honda IPC, HA-420, Chapter 72-10-01, Fig. 1',
    partNumber: 'HF120-72-010',
    description: 'GE Honda HF120 Turbofan Engine Assembly — Complete',
    crossReferences: ['GE P/N: HF120-1A', 'Alternate: HF120-1B (Elite S config)', 'CAGE Code: 27914'],
  },
  {
    id: '3',
    category: 'inspection_card',
    relevanceScore: 0.91,
    citation: 'Honda ALIM Rev J, Phase 2 Task Card 72-001',
    cardNumber: 'IC-72-001',
    task: 'GE Honda HF120 engine borescope inspection — HPT Stage 1 and LPT Stage 1/2 blades',
    interval: 'Every 150 flight hours (Phase 2) or at 600-hr major inspection',
  },
  {
    id: '4',
    category: 'ata_chapter',
    relevanceScore: 0.88,
    citation: 'ATA Spec 100 / iSpec 2200',
    chapter: 'ATA 72',
    title: 'Engine — Turbine/Turboprop',
    chapterDescription: 'Covers the turbine engine including core engine, compressor section, combustion section, turbine section, accessory drives, and engine build-up. For HondaJet HA-420: GE Honda HF120 turbofan, rated at 2,050 lbf thrust.',
  },
  {
    id: '5',
    category: 'non_routine',
    relevanceScore: 0.85,
    citation: 'NRI-2026-0042, N420HJ, 2026-01-15',
    nrDescription: 'Found nick on HPT Stage 1 blade during borescope inspection. Blade damage within serviceable limits per GE Honda CSP Chapter 72-51. Blended per repair procedure. Document for trend monitoring.',
    tailNumber: 'N420HJ',
    date: '2026-01-15',
  },
  {
    id: '6',
    category: 'manual_reference',
    relevanceScore: 0.83,
    citation: 'Honda HA-420 AMM, Rev J, Chapter 79-20-00, Page 101',
    document: 'HA-420 Aircraft Maintenance Manual (AMM)',
    section: 'Chapter 79-20-00 — Engine Oil System',
    page: 'Page 101–108',
    excerpt: 'Engine oil servicing procedure for GE Honda HF120. Use Mobil Jet Oil II or approved equivalent. Oil capacity: 4.8 quarts per engine. Check oil level with engine cold, aircraft level.',
  },
  {
    id: '7',
    category: 'part_number',
    relevanceScore: 0.80,
    citation: 'Honda IPC, HA-420, Chapter 79-20-01, Fig. 3',
    partNumber: 'HF120-79-010',
    description: 'Oil Filter Element — GE Honda HF120 Engine',
    crossReferences: ['GE P/N: 2956432-1', 'Pall P/N: HC9020FKS4H', 'Replacement interval: 300 hrs or at Phase 2'],
  },
  {
    id: '8',
    category: 'inspection_card',
    relevanceScore: 0.78,
    citation: 'Honda ALIM Rev J, Phase 2 Task Card 79-001',
    cardNumber: 'IC-79-001',
    task: 'Engine oil system service, filter replacement, and spectrometric oil analysis (SOAP)',
    interval: 'Every 150 flight hours (Phase 2)',
  },
  {
    id: '9',
    category: 'ata_chapter',
    relevanceScore: 0.75,
    citation: 'ATA Spec 100 / iSpec 2200',
    chapter: 'ATA 79',
    title: 'Engine Oil',
    chapterDescription: 'Covers the engine lubricating oil systems including oil tanks, oil pumps, oil coolers, oil filters, and oil indicating systems. For HondaJet: integrated into GE Honda HF120 accessory gearbox module.',
  },
  {
    id: '10',
    category: 'non_routine',
    relevanceScore: 0.72,
    citation: 'NRI-2025-0187, N523HE, 2025-12-08',
    nrDescription: 'Elevated iron and copper content detected in left engine oil analysis (SOAP). Iron: 12 ppm (limit 15 ppm), Copper: 8 ppm (limit 10 ppm). Reduced oil change interval to 75 hrs per GE Honda guidance. Trend monitoring initiated.',
    tailNumber: 'N523HE',
    date: '2025-12-08',
  },
]

// ── Component ───────────────────────────────────────────────────────────────

export function SearchPage() {
  const [query, setQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState<FilterCategory>('all')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([])
      setHasSearched(false)
      return
    }

    setLoading(true)
    setHasSearched(true)

    try {
      const data = await api.get<SearchResult[]>(
        `/api/search?q=${encodeURIComponent(searchQuery)}&category=${activeFilter}`
      )
      if (data && Array.isArray(data)) {
        setResults(data)
      } else {
        // Fall back to filtered sample data for demo
        const filtered = sampleResults.filter((r) => {
          const searchLower = searchQuery.toLowerCase()
          const matchesQuery =
            r.citation.toLowerCase().includes(searchLower) ||
            r.document?.toLowerCase().includes(searchLower) ||
            r.section?.toLowerCase().includes(searchLower) ||
            r.excerpt?.toLowerCase().includes(searchLower) ||
            r.partNumber?.toLowerCase().includes(searchLower) ||
            r.description?.toLowerCase().includes(searchLower) ||
            r.task?.toLowerCase().includes(searchLower) ||
            r.nrDescription?.toLowerCase().includes(searchLower) ||
            r.chapter?.toLowerCase().includes(searchLower) ||
            r.title?.toLowerCase().includes(searchLower) ||
            r.chapterDescription?.toLowerCase().includes(searchLower)
          return matchesQuery
        })
        setResults(filtered)
      }
    } catch {
      // Fall back to local sample filtering
      const searchLower = searchQuery.toLowerCase()
      const filtered = sampleResults.filter((r) => {
        const matchesQuery =
          r.citation.toLowerCase().includes(searchLower) ||
          r.document?.toLowerCase().includes(searchLower) ||
          r.section?.toLowerCase().includes(searchLower) ||
          r.excerpt?.toLowerCase().includes(searchLower) ||
          r.partNumber?.toLowerCase().includes(searchLower) ||
          r.description?.toLowerCase().includes(searchLower) ||
          r.task?.toLowerCase().includes(searchLower) ||
          r.nrDescription?.toLowerCase().includes(searchLower) ||
          r.chapter?.toLowerCase().includes(searchLower) ||
          r.title?.toLowerCase().includes(searchLower) ||
          r.chapterDescription?.toLowerCase().includes(searchLower)
        return matchesQuery
      })
      setResults(filtered)
    } finally {
      setLoading(false)
    }
  }, [activeFilter])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    performSearch(query)
  }

  const handleClear = () => {
    setQuery('')
    setResults([])
    setHasSearched(false)
    setActiveFilter('all')
  }

  // Re-filter when category changes and we have a query
  useEffect(() => {
    if (query.trim() && hasSearched) {
      performSearch(query)
    }
  }, [activeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredResults = activeFilter === 'all'
    ? results
    : results.filter((r) => r.category === activeFilter)

  const getCategoryBorderColor = (category: ResultCategory) => {
    switch (category) {
      case 'manual_reference':
        return 'border-l-blue-500'
      case 'part_number':
        return 'border-l-green-500'
      case 'inspection_card':
        return 'border-l-orange-500'
      case 'non_routine':
        return 'border-l-red-500'
      case 'ata_chapter':
        return 'border-l-purple-500'
    }
  }

  const getCategoryBadge = (category: ResultCategory) => {
    switch (category) {
      case 'manual_reference':
        return <Badge variant="info">Manual Reference</Badge>
      case 'part_number':
        return <Badge variant="success">Part Number</Badge>
      case 'inspection_card':
        return <Badge variant="warning">Inspection Card</Badge>
      case 'non_routine':
        return <Badge variant="destructive">Non-Routine</Badge>
      case 'ata_chapter':
        return (
          <Badge className="border-transparent bg-purple-100 text-purple-800">
            ATA Chapter
          </Badge>
        )
    }
  }

  const getCategoryIcon = (category: ResultCategory) => {
    switch (category) {
      case 'manual_reference':
        return <FileText className="h-4 w-4 text-blue-600" />
      case 'part_number':
        return <Hash className="h-4 w-4 text-green-600" />
      case 'inspection_card':
        return <ClipboardCheck className="h-4 w-4 text-orange-600" />
      case 'non_routine':
        return <AlertTriangle className="h-4 w-4 text-red-600" />
      case 'ata_chapter':
        return <BookOpen className="h-4 w-4 text-purple-600" />
    }
  }

  const groupedResults = filteredResults.reduce<Record<ResultCategory, SearchResult[]>>(
    (acc, result) => {
      if (!acc[result.category]) {
        acc[result.category] = []
      }
      acc[result.category].push(result)
      return acc
    },
    {} as Record<ResultCategory, SearchResult[]>
  )

  const categoryOrder: ResultCategory[] = [
    'manual_reference',
    'part_number',
    'inspection_card',
    'non_routine',
    'ata_chapter',
  ]

  const getCategoryLabel = (category: ResultCategory) => {
    switch (category) {
      case 'manual_reference':
        return 'Manual References'
      case 'part_number':
        return 'Part Numbers'
      case 'inspection_card':
        return 'Inspection Cards'
      case 'non_routine':
        return 'Non-Routine Items'
      case 'ata_chapter':
        return 'ATA Chapters'
    }
  }

  const renderResult = (result: SearchResult) => {
    switch (result.category) {
      case 'manual_reference':
        return (
          <div className="space-y-1.5">
            <p className="font-semibold text-gray-900">{result.document}</p>
            <p className="text-sm font-medium text-blue-700">{result.section}</p>
            <p className="text-xs text-gray-500">{result.page}</p>
            {result.excerpt && (
              <p className="text-sm text-gray-600 leading-relaxed">{result.excerpt}</p>
            )}
          </div>
        )
      case 'part_number':
        return (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-bold text-green-700">{result.partNumber}</span>
            </div>
            <p className="text-sm text-gray-700">{result.description}</p>
            {result.crossReferences && result.crossReferences.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {result.crossReferences.map((ref, idx) => (
                  <span
                    key={idx}
                    className="inline-flex rounded bg-green-50 px-2 py-0.5 text-xs text-green-700 border border-green-200"
                  >
                    {ref}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
      case 'inspection_card':
        return (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-orange-700">{result.cardNumber}</span>
            </div>
            <p className="text-sm text-gray-700">{result.task}</p>
            <p className="text-xs font-medium text-orange-600">Interval: {result.interval}</p>
          </div>
        )
      case 'non_routine':
        return (
          <div className="space-y-1.5">
            <div className="flex items-center gap-3 text-sm">
              <span className="font-mono font-semibold text-gray-900">{result.tailNumber}</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-500">{result.date}</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{result.nrDescription}</p>
          </div>
        )
      case 'ata_chapter':
        return (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-bold text-purple-700">{result.chapter}</span>
              <span className="text-gray-400">—</span>
              <span className="font-semibold text-gray-900">{result.title}</span>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{result.chapterDescription}</p>
          </div>
        )
    }
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Search</h1>
        <p className="text-gray-600">Search across manuals, part numbers, inspection cards, and fleet records</p>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <Input
                className="h-14 pl-12 pr-24 text-lg rounded-xl border-2 focus-visible:ring-2"
                placeholder="Search manuals, parts, inspection cards, ATA chapters..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {query && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={handleClear}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                <Button type="submit" size="sm" className="h-10">
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </div>
            </div>
          </form>

          {/* Category Filters */}
          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1">
            <Filter className="h-4 w-4 text-gray-400 shrink-0" />
            {categoryFilters.map((filter) => {
              const Icon = filter.icon
              const count = filter.key === 'all'
                ? results.length
                : results.filter((r) => r.category === filter.key).length
              const isActive = activeFilter === filter.key

              return (
                <button
                  key={filter.key}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? filter.color + ' ring-2 ring-offset-1 ring-gray-300'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveFilter(filter.key)}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {filter.label}
                  {hasSearched && (
                    <span className={`rounded-full px-1.5 py-0.5 text-xs ${
                      isActive ? 'bg-white/50' : 'bg-gray-200'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500">Searching across all sources...</span>
        </div>
      ) : hasSearched && filteredResults.length === 0 ? (
        /* No Results State */
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Search className="h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No results found</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-md text-center">
              No matches found for "{query}"
              {activeFilter !== 'all' && ` in ${getCategoryLabel(activeFilter)}`}.
            </p>
            <div className="mt-6 space-y-2 text-sm text-gray-500">
              <p className="font-medium">Suggestions:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Check your spelling or try different keywords</li>
                <li>Try an ATA chapter number (e.g., "ATA 72" for engine)</li>
                <li>Search by part number (e.g., "HF120-72-010")</li>
                <li>Use broader terms (e.g., "engine" instead of "turbine blade")</li>
                <li>Clear the category filter to search across all types</li>
              </ul>
            </div>
            {activeFilter !== 'all' && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setActiveFilter('all')}
              >
                Clear Filter
              </Button>
            )}
          </CardContent>
        </Card>
      ) : hasSearched ? (
        /* Results Grouped by Type */
        <ScrollArea className="h-[calc(100vh-400px)] min-h-[400px]">
          <div className="space-y-6 pr-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''} found
                {activeFilter !== 'all' && ` in ${getCategoryLabel(activeFilter)}`}
              </p>
            </div>

            {categoryOrder.map((category) => {
              const categoryResults = groupedResults[category]
              if (!categoryResults || categoryResults.length === 0) return null

              return (
                <div key={category}>
                  <div className="mb-3 flex items-center gap-2">
                    {getCategoryIcon(category)}
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      {getCategoryLabel(category)}
                    </h2>
                    <Badge variant="secondary" className="text-xs">
                      {categoryResults.length}
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    {categoryResults.map((result) => (
                      <Card
                        key={result.id}
                        className={`border-l-4 ${getCategoryBorderColor(result.category)} hover:shadow-md transition-shadow cursor-pointer`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {renderResult(result)}
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              {getCategoryBadge(result.category)}
                              <span className="text-xs text-gray-400 font-mono">
                                {Math.round(result.relevanceScore * 100)}% match
                              </span>
                            </div>
                          </div>
                          {/* Citation */}
                          <div className="mt-3 flex items-center justify-between border-t pt-2">
                            <div className="flex items-center gap-1.5 text-xs text-gray-400">
                              <Info className="h-3 w-3" />
                              <span>{result.citation}</span>
                            </div>
                            <Button size="sm" variant="ghost" className="h-7 text-xs">
                              View Source
                              <ChevronRight className="ml-1 h-3 w-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </ScrollArea>
      ) : (
        /* Initial State — No Search Yet */
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setQuery('GE Honda HF120 engine'); performSearch('GE Honda HF120 engine') }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-100 p-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">GE Honda HF120 Engine</p>
                  <p className="text-xs text-gray-500">AMM, CMM, and service bulletins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setQuery('ATA 32 landing gear'); performSearch('ATA 32 landing gear') }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-purple-100 p-2">
                  <BookOpen className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">ATA 32 Landing Gear</p>
                  <p className="text-xs text-gray-500">Inspection and overhaul procedures</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setQuery('Garmin G3000 avionics'); performSearch('Garmin G3000 avionics') }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-green-100 p-2">
                  <Hash className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Garmin G3000 Avionics</p>
                  <p className="text-xs text-gray-500">Flight deck and navigation</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setQuery('Phase 2 inspection cards'); performSearch('Phase 2 inspection cards') }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-orange-100 p-2">
                  <ClipboardCheck className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Phase 2 Inspection Cards</p>
                  <p className="text-xs text-gray-500">Engine and powerplant tasks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setQuery('oil analysis SOAP'); performSearch('oil analysis SOAP') }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-red-100 p-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Oil Analysis / SOAP</p>
                  <p className="text-xs text-gray-500">Non-routine findings and trends</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setQuery('HF120-79-010 oil filter'); performSearch('HF120-79-010 oil filter') }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-gray-100 p-2">
                  <ExternalLink className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">HF120-79-010 Oil Filter</p>
                  <p className="text-xs text-gray-500">Part number cross-reference</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
